// Signing in a TUNE MFD from a phone: the MFD shows a code, and a QR code for this page with the code in it
// (`/pair?code=`). The owner sees what's waiting and how long the code lasts, chooses the car it's in (or a new one)
// and whose settings it uses, and signs it in, or doesn't. On a phone the choice stays at hand, in a bar at the foot.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { compactUserCode, readUserCode, type components } from '@tune/shared';
import { useId, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, data, problem } from '../../api';
import { useAuth } from '../../auth';
import { describeCar } from '../../lib/car';
import { deviceName } from '../../lib/words';
import { usePageTitle } from '../../shell/title';
import {
  Banner,
  Button,
  ButtonLink,
  ListChoice,
  ListSection,
  PairCodeField,
  Spinner,
  TextField,
  useNow,
} from '../../ui';
import { CheckCircleIcon, QrCodeIcon, XCircleIcon } from '../../ui/icons';
import s from './Pair.module.css';

type Pairing = components['schemas']['Pairing'];
type Row = components['schemas']['VehicleRow'];
type Decision = components['schemas']['PairDecision'];
type Decided = components['schemas']['PairDecided'];

const NEW = 'new';
const CODE_HINT = 'Eight letters, like BCDF-GHJK.';
const NOT_A_CODE = "That isn't a code an MFD shows. It's eight letters, like BCDF-GHJK.";

export function Pair() {
  const [params, setParams] = useSearchParams();
  const typed = params.get('code');
  const code = readUserCode(typed ?? '');
  if (!code) {
    return <EnterCode typed={typed} onCode={(c) => setParams({ code: compactUserCode(c) })} />;
  }
  return <Approve key={code} code={code} onAnother={() => setParams({})} />;
}

/** The page's heading, which the browser names it after. */
function Heading({ children, tab }: { children: string; tab?: string }) {
  usePageTitle(tab ?? children);
  return (
    <h1 className={s.title} tabIndex={-1}>
      {children}
    </h1>
  );
}

function EnterCode({ typed, onCode }: { typed: string | null; onCode: (code: string) => void }) {
  const [value, setValue] = useState(typed ?? '');
  const [error, setError] = useState<string | null>(typed ? NOT_A_CODE : null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const code = readUserCode(value);
    if (!code) {
      setError(NOT_A_CODE);
      return;
    }
    onCode(code);
  };
  return (
    <div className={s.page}>
      <QrCodeIcon className={s.symbol} size={56} aria-hidden="true" />
      <Heading tab="Sign in an MFD">Sign in a TUNE MFD</Heading>
      <p className={s.lead}>
        On the MFD, open Settings, then TUNE account. Scan its code with your phone's camera, or type the code it shows
        here.
      </p>
      <form className={s.form} onSubmit={submit} noValidate>
        <PairCodeField
          label="Code"
          hint={CODE_HINT}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          error={error}
          autoFocus
        />
        <Button type="submit" size="large" wide>
          Continue
        </Button>
      </form>
    </div>
  );
}

function Approve({ code, onAnother }: { code: string; onAnother: () => void }) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const path = { params: { path: { userCode: code } } };
  const pairing = useQuery({
    queryKey: ['pairing', code],
    queryFn: () => data(api.GET('/pair/{userCode}', path)),
    retry: false,
    staleTime: Infinity,
  });
  const decide = useMutation({
    mutationFn: (body: Decision) => data(api.POST('/pair/{userCode}', { ...path, body })),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vehicles'] }),
        queryClient.invalidateQueries({ queryKey: ['devices'] }),
      ]);
    },
  });
  if (pairing.isPending) {
    return (
      <div className={s.page}>
        <Spinner size="large" label="Looking for the code" />
      </div>
    );
  }
  if (pairing.isError) {
    const gone = pairing.error instanceof ApiError && pairing.error.status === 404;
    return (
      <div className={s.page}>
        <XCircleIcon className={s.symbol} size={56} aria-hidden="true" />
        <Heading>{gone ? "That code isn't waiting" : 'Sign in a TUNE MFD'}</Heading>
        <p className={s.lead}>
          {gone
            ? `No TUNE MFD is waiting with ${code}. A code lasts ten minutes and works once: show a new one on the MFD, then enter it.`
            : problem(pairing.error)}
        </p>
        {gone ? (
          <Button size="large" wide onClick={onAnother}>
            Enter another code
          </Button>
        ) : (
          <Button size="large" wide onClick={() => void pairing.refetch()}>
            Try again
          </Button>
        )}
      </div>
    );
  }
  if (decide.isSuccess) {
    return <Done decided={decide.data} pairing={pairing.data} />;
  }
  return (
    <Decide
      pairing={pairing.data}
      busy={decide.isPending}
      error={decide.error}
      onDecide={(decision) => decide.mutate(decision)}
    />
  );
}

/** The car to offer first: the one this device was in, else the first without an MFD. */
function firstChoice(cars: Row[]): string {
  return (cars.find((c) => c.mfd === 'this') ?? cars.find((c) => c.mfd === 'none') ?? cars[0])?.id ?? NEW;
}

/** How long a code has left, in words: "8 minutes", "less than a minute". */
function leftText(expiresAt: string, now: number): string | null {
  const left = Date.parse(expiresAt) - now;
  if (left <= 0) {
    return null;
  }
  const minutes = Math.floor(left / 60_000);
  return minutes < 1 ? 'less than a minute' : minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

function Decide({
  pairing,
  busy,
  error,
  onDecide,
}: {
  pairing: Pairing;
  busy: boolean;
  error: Error | null;
  onDecide: (decision: Decision) => void;
}) {
  const group = useId();
  const now = useNow(15_000);
  const mfd = pairing.device.kind === 'mfd';
  const thing = mfd ? 'MFD' : 'device';
  const [choice, setChoice] = useState(() => firstChoice(pairing.vehicles));
  const [nickname, setNickname] = useState('');
  const [unnamed, setUnnamed] = useState(false);
  const [settings, setSettings] = useState<'vehicle' | 'device'>('vehicle');
  const chosen = pairing.vehicles.find((c) => c.id === choice);
  const asksSettings = chosen?.hasSettings === true;
  const left = leftText(pairing.expiresAt, now);

  const approve = (e: FormEvent) => {
    e.preventDefault();
    if (choice === NEW) {
      const name = nickname.trim();
      if (!name) {
        setUnnamed(true);
        return;
      }
      onDecide({ decision: 'approve', newVehicle: { nickname: name }, settings: 'device' });
      return;
    }
    onDecide({ decision: 'approve', vehicleId: choice, settings: asksSettings ? settings : 'device' });
  };

  const named = deviceName(pairing.device);
  return (
    <form className={s.decide} onSubmit={approve} noValidate>
      <div className={s.page}>
        <Heading tab="Sign in an MFD">{mfd ? 'Sign in a TUNE MFD' : 'Sign in a device'}</Heading>
        <div className={s.device}>
          <span className={s.deviceName}>{named}</span>
          <span className={s.deviceMeta}>
            Waiting to sign in to your TUNE account with <span className={s.code}>{pairing.userCode}</span>
          </span>
          <span className={left ? s.deviceMeta : s.late}>
            {left ? `The code works for ${left} more.` : 'The code has run out. Show a new one on the MFD.'}
          </span>
        </div>
        {error && <Banner tone="error">{decideProblem(error, thing)}</Banner>}
        <ListSection header={mfd ? 'Which car is it in?' : 'Which car is it for?'} level={2}>
          {pairing.vehicles.map((car) => (
            <ListChoice
              key={car.id}
              name={`${group}-car`}
              value={car.id}
              checked={choice === car.id}
              onChange={() => setChoice(car.id)}
              label={car.nickname}
              subtitle={
                [
                  describeCar(car),
                  mfd && car.mfd === 'other' ? "Has an MFD: that one stays signed in, and asks which car it's in" : '',
                  car.mfd === 'this' ? `This ${thing}'s car` : '',
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
            />
          ))}
          <ListChoice
            name={`${group}-car`}
            value={NEW}
            checked={choice === NEW}
            onChange={() => setChoice(NEW)}
            label="A new car"
            subtitle={`It starts with this ${thing}'s settings`}
          />
        </ListSection>
        {choice === NEW && (
          <TextField
            label="What do you call it?"
            hint="Like R34 or Track car. You can change it later."
            value={nickname}
            maxLength={40}
            autoComplete="off"
            onChange={(e) => {
              setNickname(e.target.value);
              setUnnamed(false);
            }}
            error={unnamed ? 'Give the car a name, like R34 or Track car.' : undefined}
          />
        )}
        {chosen && asksSettings && (
          <ListSection header="Which settings should it use?" level={2}>
            <ListChoice
              name={`${group}-settings`}
              value="vehicle"
              checked={settings === 'vehicle'}
              onChange={() => setSettings('vehicle')}
              label={`${chosen.nickname}'s settings`}
              subtitle={`The settings saved for ${chosen.nickname} replace this ${thing}'s`}
            />
            <ListChoice
              name={`${group}-settings`}
              value="device"
              checked={settings === 'device'}
              onChange={() => setSettings('device')}
              label={`This ${thing}'s settings`}
              subtitle={`They replace ${chosen.nickname}'s, whose earlier settings stay in its history`}
            />
          </ListSection>
        )}
      </div>
      <div className={s.bar}>
        <Button type="submit" size="large" wide busy={busy}>
          Sign in the {thing}
        </Button>
        <Button variant="plain" wide disabled={busy} onClick={() => onDecide({ decision: 'deny' })}>
          Don't sign it in
        </Button>
      </div>
    </form>
  );
}

function decideProblem(error: Error, thing: string): string {
  if (error instanceof ApiError && error.status === 404) {
    return `That code has run out, or was used. Show a new code on the ${thing}, then enter it.`;
  }
  if (error instanceof ApiError && error.code === 'quota_exceeded') {
    return 'Your account has as many cars as it can. Choose one of your cars, or delete one in your garage first.';
  }
  return problem(error);
}

function Done({ decided, pairing }: { decided: Decided; pairing: Pairing }) {
  const thing = pairing.device.kind === 'mfd' ? 'TUNE MFD' : 'device';
  const approved = decided.decision === 'approve';
  return (
    <div className={s.page}>
      {approved ? (
        <CheckCircleIcon className={s.done} size={64} weight="fill" aria-hidden="true" />
      ) : (
        <XCircleIcon className={s.symbol} size={64} aria-hidden="true" />
      )}
      <Heading>{approved ? `The ${thing} is signing in` : `The ${thing} wasn't signed in`}</Heading>
      <p className={s.lead}>
        {approved
          ? `It's signing in${decided.vehicle ? ` to ${decided.vehicle.nickname}` : ''}, and is ready in a few seconds.`
          : 'Nothing changed. Without your approval here, nobody can sign in with that code.'}
      </p>
      <div className={s.doneActions}>
        {approved && decided.vehicle ? (
          <ButtonLink to={`/garage/${decided.vehicle.id}`} size="large" wide>
            Go to {decided.vehicle.nickname}
          </ButtonLink>
        ) : null}
        <ButtonLink to="/" variant={approved && decided.vehicle ? 'gray' : 'filled'} size="large" wide>
          Home
        </ButtonLink>
      </div>
    </div>
  );
}
